import React, { useMemo, useCallback } from 'react';
import styled from '@emotion/styled';
import useWindowSize from '@hooks/useWindowSize';
import Typography from '@unicons-base/Typography/Typography';
import Image from '@base/Image';
import UN_ICONS_TYPOGRAPHY_VARIANT from 'types/unacademyIcons/typography.type';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import { DECORATED_TITLE_TYPE } from '@constants/unacademyIcons/anyfeed.constants';

interface AnyfeedSectionHeaderProps {
  title: string;
  titleType: string | null;
  iconSrc?: string;
  hasActiveSubscription?: boolean;
}

type SectionHeaderType = {
  $hasLightMargin: boolean;
  $isDecorated?: boolean;
};

const SectionHeader = styled.div<SectionHeaderType>`
  display: flex;
  justify-content: center;
  align-items: ${({ $hasLightMargin, $isDecorated }) =>
    $hasLightMargin || !$isDecorated ? 'flex-start' : 'center'};
  flex-direction: column;
  margin-bottom: ${({ $hasLightMargin, $isDecorated }) =>
    $hasLightMargin || !$isDecorated
      ? `${Spacings.SPACING_6B}`
      : `${Spacings.SPACING_16B}`};
  ${mediaQuery.uptoTablet} {
    justify-content: flex-start;
    align-items: center;
    flex-direction: row;
    margin-bottom: ${Spacings.SPACING_5B};
    padding: 0px ${Spacings.SPACING_4B};
  }
`;
const IconWrapper = styled.div`
  margin-bottom: ${Spacings.SPACING_7B};
  margin-right: 0px;
  ${mediaQuery.uptoTablet} {
    margin-bottom: 0px;
    margin-right: ${Spacings.SPACING_3B};
  }
`;

const DividerImage = styled(Image)<{ $isReverse?: boolean }>`
  transform: ${({ $isReverse }) =>
    $isReverse ? 'rotate(180deg)' : 'rotate(0deg)'};
  padding: 0 ${Spacings.SPACING_6B} !important;
  ${mediaQuery.uptoMobile} {
    width: 100%;
    padding: 0 ${Spacings.SPACING_3B} !important;
  }
`;

const HeadingWrapper = styled.div<{ $isDecorated?: boolean }>`
  display: flex;
  align-items: center;
  margin: ${({ $isDecorated }) => $isDecorated && 'auto'};
  width: ${({ $isDecorated }) => !$isDecorated && '100%'};
`;

const StyledTypography = styled(Typography)`
  ${mediaQuery.uptoMobile} {
    text-align: center;
    width: 100%;
    max-width: fit-content;
    font-size: ${Fonts.H4};
  }
`;

const AnyfeedSectionHeader: React.FC<AnyfeedSectionHeaderProps> = ({
  title,
  titleType,
  iconSrc,
  hasActiveSubscription
}) => {
  const { isMobile } = useWindowSize();
  const size = useMemo(() => (isMobile ? '24px' : '48px'), [isMobile]);

  const isDecoratedTitle = titleType === DECORATED_TITLE_TYPE;

  const titleVariant = useMemo(
    () =>
      isDecoratedTitle
        ? UN_ICONS_TYPOGRAPHY_VARIANT.H2
        : UN_ICONS_TYPOGRAPHY_VARIANT.H3,
    [isDecoratedTitle]
  );

  const renderDivider = useCallback(
    (isReverse) => (
      <DividerImage
        src="/static/images/icons-heading-divider.svg"
        alt="Divider"
        layout="intrinsic"
        width="166px"
        height="14.76px"
        unoptimized
        $isReverse={isReverse}
      />
    ),
    []
  );

  return (
    <SectionHeader
      $hasLightMargin={hasActiveSubscription}
      $isDecorated={isDecoratedTitle}
    >
      {iconSrc && !hasActiveSubscription && (
        <IconWrapper>
          <Image
            src={iconSrc}
            alt={title}
            layout="intrinsic"
            width={size}
            height={size}
            unoptimized
          />
        </IconWrapper>
      )}

      <HeadingWrapper $isDecorated={isDecoratedTitle}>
        {isDecoratedTitle && renderDivider(false)}
        {title && (
          <StyledTypography
            variant={titleVariant}
            desktopMargin="0px"
            mobileMargin="0px"
          >
            {title}
          </StyledTypography>
        )}
        {isDecoratedTitle && renderDivider(true)}
      </HeadingWrapper>
    </SectionHeader>
  );
};

AnyfeedSectionHeader.displayName = 'AnyfeedSectionHeader';

export default AnyfeedSectionHeader;
